/**
 * The two building blocks of every synthesised interface cue (`cues.ts`):
 * an enveloped oscillator and an enveloped filtered noise burst.
 *
 * Owns: node construction and the envelope. Must not: know any cue, keep
 * state, or connect to anything but the `out` it is handed. Every source is
 * scheduled with an explicit stop, so nothing built here outlives its cue.
 */
import { noiseSource } from './synth';

/** Seconds to peak. Short enough to read as a strike, long enough not to click. */
const ATTACK_S = 0.004;
/** Silence floor for the exponential ramp, which cannot reach 0. */
const FLOOR = 0.0001;
/** Seconds a source runs past its envelope before it is stopped. */
const STOP_PAD_S = 0.02;

interface Shape {
  /** Peak gain. */
  peak: number;
  /** Envelope length, seconds; the cue's own sound ends here. */
  dur: number;
  /** Seconds after the cue's start. */
  delay?: number;
  attack?: number;
}

export interface ToneSpec extends Shape {
  type: OscillatorType;
  hz: number;
  /** Glides to this by the end of the envelope. */
  toHz?: number;
}

export interface NoiseSpec extends Shape {
  filter: BiquadFilterType;
  hz: number;
  toHz?: number;
  q?: number;
}

function envelope(ctx: AudioContext, out: AudioNode, t0: number, s: Shape): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(FLOOR, t0);
  g.gain.linearRampToValueAtTime(s.peak, t0 + (s.attack ?? ATTACK_S));
  g.gain.exponentialRampToValueAtTime(FLOOR, t0 + s.dur);
  g.connect(out);
  return g;
}

export function tone(ctx: AudioContext, out: AudioNode, at: number, s: ToneSpec): void {
  const t0 = at + (s.delay ?? 0);
  const osc = ctx.createOscillator();
  osc.type = s.type;
  osc.frequency.setValueAtTime(s.hz, t0);
  if (s.toHz !== undefined) osc.frequency.exponentialRampToValueAtTime(s.toHz, t0 + s.dur);
  osc.connect(envelope(ctx, out, t0, s));
  osc.start(t0);
  osc.stop(t0 + s.dur + STOP_PAD_S);
}

export function noise(ctx: AudioContext, out: AudioNode, at: number, s: NoiseSpec): void {
  const t0 = at + (s.delay ?? 0);
  const src = noiseSource(ctx, false);
  const filter = ctx.createBiquadFilter();
  filter.type = s.filter;
  filter.Q.value = s.q ?? 1;
  filter.frequency.setValueAtTime(s.hz, t0);
  if (s.toHz !== undefined) filter.frequency.exponentialRampToValueAtTime(s.toHz, t0 + s.dur);
  src.connect(filter);
  filter.connect(envelope(ctx, out, t0, s));
  src.start(t0);
  src.stop(t0 + s.dur + STOP_PAD_S);
}
